import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import SolDivider from '../components/ui/SolDivider';
import { EventCard, EmptyState } from '../components/ui/Cards';
import { motion } from 'framer-motion';

export default function Eventos() {
  const { eventos, CAPITULOS, rsvps, toggleRsvp, currentUser } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [tab, setTab] = useState('upcoming'); // 'upcoming', 'past'

  // Sync state with URL query parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam) setTab(tabParam);
  }, [location.search]);

  // Navigate and clear parameters when clicking tabs manually
  const handleTabChange = (newTab) => {
    setTab(newTab);
    navigate(`/eventos?tab=${newTab}`, { replace: true });
  };

  const upcoming = eventos.filter(e => !e.isPast);
  const past = eventos.filter(e => e.isPast);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-10">
      
      {/* Hero Section */}
      <div className="relative">
        <div className="absolute -inset-x-10 -top-10 h-[250px] bg-[#74ACDF]/5 -z-10 rounded-b-[100px]" />
        
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <span className="label-technical text-[#F6B40E] mb-2 block">Agenda del Movimiento</span>
            <h1 className="text-4xl md:text-6xl font-editorial font-bold text-[#1B2A4E] leading-tight">
              Encuentros <span className="text-[#74ACDF] italic">reales.</span>
            </h1>
            <p className="text-lg text-stone-500 mt-4 max-w-lg">
              Conectá cara a cara en tu ciudad o sumate a los eventos virtuales.
            </p>
          </div>
        </div>

        {/* Tabs Float */}
        <div className="mt-10 flex bg-white p-1.5 rounded-2xl w-fit shadow-lg shadow-stone-200/50 border border-stone-100 overflow-x-auto no-scrollbar">
          <button 
            onClick={() => handleTabChange('upcoming')} 
            className={`px-6 md:px-8 py-3 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${tab === 'upcoming' ? 'bg-[#1B2A4E] text-[#F6B40E] shadow-md' : 'text-stone-500 hover:text-[#1B2A4E]'}`}
          >
            Próximos
          </button>
          <button 
            onClick={() => handleTabChange('past')} 
            className={`px-6 md:px-8 py-3 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${tab === 'past' ? 'bg-[#1B2A4E] text-[#F6B40E] shadow-md' : 'text-stone-500 hover:text-[#1B2A4E]'}`}
          >
            Pasados
          </button>
        </div>
      </div>

      <SolDivider />

      <div className="space-y-6">
        {tab === 'upcoming' && (
          upcoming.length > 0 ? upcoming.map((e, idx) => (
            <EventCard 
              key={e.id} 
              e={e} 
              delay={idx * 0.1} 
              capituloName={CAPITULOS.find(c => c.id === e.capitulo_id)?.nombre} 
              isAttending={rsvps.some(r => r.eventId === e.id && r.memberId === currentUser.id)} 
              toggleRsvp={toggleRsvp} 
              currentUser={currentUser} 
            />
          )) : <EmptyState icon="Calendar" title="Sin eventos próximos" message="La agenda está libre por ahora." />
        )}
        
        {tab === 'past' && (
          past.length > 0 ? past.map((e, idx) => (
            <EventCard 
              key={e.id} 
              e={e} 
              delay={idx * 0.1} 
              capituloName={CAPITULOS.find(c => c.id === e.capitulo_id)?.nombre} 
              isAttending={false} 
              toggleRsvp={() => {}} 
              currentUser={currentUser} 
            />
          )) : <EmptyState icon="History" title="Sin historial" message="Aún no hay eventos pasados registrados." />
        )}
      </div>

      {/* Bottom Gradient Band CTA */}
      <div className="mt-20 p-12 bg-gradient-to-br from-[#74ACDF] to-[#1B2A4E] rounded-[40px] text-white text-center relative overflow-hidden shadow-2xl">
        <div className="absolute top-0 right-0 w-80 h-80 bg-white/10 rounded-full -mr-40 -mt-40 blur-3xl" />
        <div className="relative z-10">
          <h2 className="text-3xl md:text-4xl font-editorial font-bold mb-4">¿Querés organizar un evento?</h2>
          <p className="text-white/80 mb-8 max-w-md mx-auto text-lg">Los eventos locales son el corazón del Movimiento. Sumá tu propuesta en tu ciudad.</p>
          <button className="bg-[#F6B40E] hover:bg-white text-[#1B2A4E] font-bold py-4 px-10 rounded-2xl transition-all hover:scale-105 shadow-xl">
            Proponer Evento
          </button>
        </div>
      </div>

    </motion.div>
  );
}